import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { UiTableComponent } from '@big-direkt/ui/table';
import { TableRowModel } from '@big-direkt/utils/shared';
import { DiagnosisCodeSearchItemListComponent } from './diagnosis-code-search-item-list.component';
import { DiagnosisCodeSearchItemTableCellLinksComponent } from './diagnosis-code-search-item-table-cell-links.component';

@Component({
    selector: 'big-service-tools-diagnosis-code-search-item',
    templateUrl: './diagnosis-code-search-item.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [UiTableComponent],
})
export class DiagnosisCodeSearchItemComponent {
    public readonly parent = input.required<string>();
    public readonly children = input.required<string[]>();
    public readonly inclusions = input.required<string[]>();
    public readonly exclusions = input.required<string[]>();
    public readonly synonyms = input.required<string[]>();

    public readonly tableRows = computed(() => {
        const cssClass = [
            'md:w-1/2',
            'group-odd/row:bg-primary-100',
            'group-odd/row:text-primary-800',
            'group-odd/row:border-r-primary-50',
            'group-even/row:bg-primary-50',
            'group-even/row:text-primary-800',
            'group-even/row:border-r-primary-100',
            'group-odd/row:dark:bg-primary-500',
            'group-odd/row:dark:text-white',
            'group-odd/row:dark:border-r-primary-800',
            'group-even/row:dark:bg-primary-800',
            'group-even/row:dark:text-white',
            'group-even/row:dark:border-r-primary-500',
            'last:mb-0!',
        ].join(' ');

        const rows: TableRowModel = [];
        const parent = this.parent();

        if (parent) {
            rows.push([
                {
                    type: 'string',
                    value: 'stDiagnosisCodeSearch.results.parents',
                    cssClass,
                },
                {
                    type: 'component',
                    cssClass,
                    value: {
                        type: DiagnosisCodeSearchItemTableCellLinksComponent,
                        inputs: {
                            links: [{
                                title: parent,
                                routerLink: [],
                                queryParams: { q: parent },
                                queryParamsHandling: 'merge',
                            }],
                        },
                    },
                },
            ]);
        }

        const children = this.children();
        if (children.length) {
            rows.push([
                {
                    type: 'string',
                    value: 'stDiagnosisCodeSearch.results.children',
                    cssClass,
                },
                {
                    type: 'component',
                    cssClass,
                    value: {
                        type: DiagnosisCodeSearchItemTableCellLinksComponent,
                        inputs: {
                            links: children.map(child => ({
                                title: child,
                                routerLink: [],
                                queryParams: { q: child },
                                queryParamsHandling: 'merge',
                            })),
                        },
                    },
                },
            ]);
        }

        const inclusions = this.inclusions();
        if (inclusions.length) {
            rows.push([
                {
                    type: 'string',
                    value: 'stDiagnosisCodeSearch.results.inclusions',
                    cssClass,
                },
                {
                    type: 'component',
                    cssClass: 'last:mb-0!',
                    value: {
                        type: DiagnosisCodeSearchItemListComponent,
                        inputs: {
                            items: inclusions,
                        },
                    },
                },
            ]);
        }

        const exclusions = this.exclusions();
        if (exclusions.length) {
            rows.push([
                {
                    type: 'string',
                    value: 'stDiagnosisCodeSearch.results.exclusions',
                    cssClass,
                },
                {
                    type: 'component',
                    cssClass,
                    value: {
                        type: DiagnosisCodeSearchItemListComponent,
                        inputs: {
                            items: exclusions,
                        },
                    },
                },
            ]);
        }

        const synonyms = this.synonyms();
        if (synonyms.length) {
            rows.push([
                {
                    type: 'string',
                    value: 'stDiagnosisCodeSearch.results.synonyms',
                    cssClass,
                },
                {
                    type: 'component',
                    cssClass,
                    value: {
                        type: DiagnosisCodeSearchItemListComponent,
                        inputs: {
                            items: synonyms,
                        },
                    },
                },
            ]);
        }

        return rows;
    });
}
